import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LogOut,
  Image as ImageIcon,
  Type,
  Save,
  Eye,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useSiteMeta } from '../context/SiteContext';
import { api } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { brand, site as defaults } from '../config/site';
import MediaLibrary from '../components/admin/MediaLibrary';
import ContentEditor from '../components/admin/ContentEditor';

const tabs = [
  { id: 'content', label: 'Content', icon: Type },
  { id: 'media', label: 'Media Library', icon: ImageIcon },
];

const AdminDashboard = () => {
  const { email, logout } = useAuth();
  const nav = useNavigate();
  const { toast } = useToast();
  const { site, refresh } = useSiteMeta();

  const [tab, setTab] = useState('content');
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(JSON.parse(JSON.stringify(site)));
  }, [site]);

  const dirty = useMemo(() => {
    if (!draft) return false;
    return JSON.stringify(draft) !== JSON.stringify(site);
  }, [draft, site]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await api.put('/admin/content', { data: draft });
      await refresh();
      toast({ title: 'Saved', description: 'Site content updated.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setDraft(JSON.parse(JSON.stringify(site)));
    toast({ title: 'Discarded', description: 'Reverted to the last saved version.' });
  };

  const resetToDefaults = () => {
    if (!window.confirm('Reset all content back to factory defaults? Media uploads are kept.')) return;
    setDraft(JSON.parse(JSON.stringify(defaults)));
  };

  const onLogout = () => {
    logout();
    nav('/admin/login');
  };

  return (
    <div className="min-h-screen bg-[#06080d] text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-white/8 bg-[#06080d]/85 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={brand.logoUrl} alt={brand.name} className="h-10 w-auto" />
            <span className="text-slate-500">/</span>
            <span className="text-sm text-white font-medium">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-brand-green-light btn-transition"
            >
              <Eye className="h-4 w-4" /> View site
            </a>
            <span className="hidden md:inline text-xs text-slate-500">{email}</span>
            <Button
              variant="outline"
              onClick={onLogout}
              className="h-9 border-white/15 bg-white/5 hover:bg-white/10 text-white btn-transition"
            >
              <LogOut className="h-4 w-4 mr-1.5" /> Logout
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-1">
            {tabs.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`px-4 py-3 text-sm flex items-center gap-2 border-b-2 btn-transition ${
                    active
                      ? 'text-white border-brand-green'
                      : 'text-slate-400 border-transparent hover:text-white'
                  }`}
                >
                  <Icon className="h-4 w-4" /> {t.label}
                </button>
              );
            })}
            <div className="ml-auto flex items-center gap-2 py-2">
              {tab === 'content' && (
                <>
                  <Button
                    variant="outline"
                    onClick={reset}
                    disabled={!dirty}
                    className="h-9 border-white/15 bg-white/5 hover:bg-white/10 text-white btn-transition disabled:opacity-50"
                  >
                    <RefreshCw className="h-4 w-4 mr-1.5" /> Discard
                  </Button>
                  <Button
                    onClick={resetToDefaults}
                    variant="outline"
                    className="h-9 border-white/15 bg-white/5 hover:bg-white/10 text-white btn-transition"
                  >
                    Reset to defaults
                  </Button>
                  <Button
                    onClick={save}
                    disabled={!dirty || saving}
                    className="h-9 bg-brand-green hover:bg-brand-green-light text-black font-medium btn-transition"
                  >
                    {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
                    {saving ? 'Saving…' : 'Save changes'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {tab === 'content' && draft && (
          <ContentEditor draft={draft} setDraft={setDraft} />
        )}
        {tab === 'media' && <MediaLibrary />}
      </main>
    </div>
  );
};

export default AdminDashboard;
